/**
 * Job phases (ADR-019) and the company's phase types. Query keys live under ["tenant", slug, "phases"];
 * a phase change also refreshes the job (its `phase_summary`) under ["tenant", slug, "sales", "jobs"].
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { registerLiveTopic } from "@/app/live/topics";

import { api, unwrap } from "../client";
import type { components } from "../generated/schema";
import { noErrorToast } from "../mutationMeta";
import type { Body } from "./operations";
import { salesKeys } from "./sales";

type S = components["schemas"];
const path = (slug: string) => ({ path: { tenant_slug: slug } });
const pathId = (slug: string, id: string) => ({ path: { tenant_slug: slug, id } });

export type Phase = S["PhaseList"];
export type PhaseDetail = S["PhaseDetail"];
export type PhaseSummary = S["PhaseSummary"];
export type PhaseType = S["PhaseType"];
export type PhaseWrite = Body<"/api/t/{tenant_slug}/job-phases/", "post">;

export const phaseKeys = {
  all: (slug: string) => ["tenant", slug, "phases"] as const,
  ofJob: (slug: string, jobId: string) => ["tenant", slug, "phases", "job", jobId] as const,
  detail: (slug: string, id: string) => ["tenant", slug, "phases", "detail", id] as const,
  types: (slug: string) => ["tenant", slug, "phases", "types"] as const,
};

// Events only say what changed: refetch phases, and the jobs that show their summary.
registerLiveTopic("phase", (_e, slug) => [phaseKeys.all(slug), salesKeys.jobs(slug)]);
registerLiveTopic("readiness", (_e, slug) => [phaseKeys.all(slug)]);

/** One job's phases in order (not paginated). */
export function useJobPhases(slug: string, jobId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: phaseKeys.ofJob(slug, jobId ?? ""),
    enabled: Boolean(jobId) && enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/jobs/{job_pk}/phases/", { params: { path: { tenant_slug: slug, job_pk: jobId! } } })),
  });
}

export type PhaseFilters = { job?: string; stage?: string; responsible?: string; open?: "true"; mine?: "true"; q?: string; page?: number };

export function usePhases(slug: string, filters: PhaseFilters, enabled = true) {
  return useQuery({
    queryKey: [...phaseKeys.all(slug), "list", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/job-phases/", { params: { ...path(slug), query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function usePhase(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: phaseKeys.detail(slug, id ?? ""),
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/job-phases/{id}/", { params: pathId(slug, id!) })),
  });
}

type Reason = Body<"/api/t/{tenant_slug}/job-phases/{id}/hold/", "post">;

/** Create, edit, hold, resume, cancel and close a phase; every answer is the phase's detail. */
export function usePhaseAction(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (
      vars:
        | { action: "create"; body: PhaseWrite }
        | { action: "update"; id: string; body: Body<"/api/t/{tenant_slug}/job-phases/{id}/", "patch"> }
        | { action: "hold" | "resume" | "cancel" | "close"; id: string; body: Reason }
        | { action: "project-manager"; id: string; body: Body<"/api/t/{tenant_slug}/job-phases/{id}/project-manager/", "post"> },
    ): Promise<PhaseDetail> => {
      switch (vars.action) {
        case "create":
          return unwrap(api.POST("/api/t/{tenant_slug}/job-phases/", { params: path(slug), body: vars.body }));
        case "update":
          return unwrap(api.PATCH("/api/t/{tenant_slug}/job-phases/{id}/", { params: pathId(slug, vars.id), body: vars.body }));
        case "hold":
          return unwrap(api.POST("/api/t/{tenant_slug}/job-phases/{id}/hold/", { params: pathId(slug, vars.id), body: vars.body }));
        case "resume":
          return unwrap(api.POST("/api/t/{tenant_slug}/job-phases/{id}/resume/", { params: pathId(slug, vars.id), body: vars.body }));
        case "cancel":
          return unwrap(api.POST("/api/t/{tenant_slug}/job-phases/{id}/cancel/", { params: pathId(slug, vars.id), body: vars.body }));
        case "project-manager":
          return unwrap(api.POST("/api/t/{tenant_slug}/job-phases/{id}/project-manager/", { params: pathId(slug, vars.id), body: vars.body }));
        default:
          return unwrap(api.POST("/api/t/{tenant_slug}/job-phases/{id}/close/", { params: pathId(slug, vars.id), body: vars.body }));
      }
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(phaseKeys.detail(slug, data.id), data);
      void qc.invalidateQueries({ queryKey: phaseKeys.all(slug) });
      void qc.invalidateQueries({ queryKey: salesKeys.jobs(slug) });
      // The PM shows on the calendar's tiles and filters.
      if (vars.action === "project-manager") void qc.invalidateQueries({ queryKey: ["tenant", slug, "schedule"] });
    },
  });
}

export function usePhaseTypes(slug: string) {
  return useQuery({
    queryKey: phaseKeys.types(slug),
    staleTime: 60_000,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/phase-types/", { params: path(slug) })),
  });
}

export function useSavePhaseType(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: { id?: string; body: Body<"/api/t/{tenant_slug}/phase-types/", "post"> }) =>
      vars.id
        ? unwrap(api.PATCH("/api/t/{tenant_slug}/phase-types/{id}/", { params: pathId(slug, vars.id), body: vars.body }))
        : unwrap(api.POST("/api/t/{tenant_slug}/phase-types/", { params: path(slug), body: vars.body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: phaseKeys.types(slug) }),
  });
}
